import { styled } from '@mui/material/styles';
import { Box, Table, TableCell, Typography } from '@mui/material';

export const ListContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  padding: '40px',
  border: '1px solid #D9D9D9',
});

export const ListHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
});

export const ListTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 700,
  color: '#111827',
  marginBottom: '24px',
});

export const TableWrapper = styled(Box)({
  marginTop: '20px',
  overflowX: 'auto',
});

// 전역 CSS(index.css)가 th:first-child 패딩을 0 으로 만들어 첫 칸이 본문과 어긋나므로 && 와 !important 로 헤더·본문 패딩을 같게 고정한다.
const CELL_PADDING = '12px 16px !important';

export const AppLinkTable = styled(Table)({
  minWidth: '860px',
  tableLayout: 'fixed',
});

export const HeaderCell = styled(TableCell)({
  '&&': {
    padding: CELL_PADDING,
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '&&.MuiTableCell-alignCenter': { textAlign: 'center' },
});

export const BodyCell = styled(TableCell)({
  '&&': {
    padding: CELL_PADDING,
    fontSize: '14px',
    textAlign: 'left',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '&&.MuiTableCell-alignCenter': { textAlign: 'center' },
});

export const CellLine = styled('div')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const EmptyText = styled(Typography)({
  padding: '60px 0',
  textAlign: 'center',
  color: '#737373',
  fontSize: '15px',
});

export const RowActions = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
});

export const StatusBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'statusColor',
})(({ statusColor }) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#fff',
  background: statusColor,
}));

export const SearchRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginBottom: '16px',
});

export const SearchInput = styled('input')({
  flex: 1,
  height: '40px',
  padding: '0 12px',
  border: '1px solid #D9D9D9',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  '&:focus': { borderColor: '#4AB3BC' },
});

export const ResultList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxHeight: '300px',
  overflowY: 'auto',
});

export const ResultItem = styled('button')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  border: '1px solid #E5E5E5',
  borderRadius: '8px',
  background: '#fff',
  fontSize: '14px',
  color: '#262626',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { borderColor: '#4AB3BC', background: '#F4FBFB' },
});

export const ResultSub = styled('span')({
  color: '#737373',
  fontSize: '13px',
});

export const CompareRow = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  gap: '12px',
});

export const CompareCard = styled(Box)({
  flex: 1,
  padding: '16px',
  border: '1px solid #E5E5E5',
  borderRadius: '8px',
  background: '#FAFAFA',
});

export const CompareHeading = styled(Typography)({
  fontSize: '13px',
  fontWeight: 700,
  color: '#4AB3BC',
  marginBottom: '8px',
});

export const CompareLine = styled(Typography)({
  fontSize: '14px',
  color: '#262626',
  lineHeight: 1.7,
  wordBreak: 'break-all',
});

export const CompareArrow = styled(Box)({
  alignSelf: 'center',
  fontSize: '20px',
  color: '#737373',
});

// 승인 창 — 맨 위 방법 고르기(기존 회원 연결 / 잠재회원 새로 등록). 고른 카드는 청록 테두리·배경
export const ChoiceRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginBottom: '20px',
});

export const ChoiceCard = styled('button', {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '6px',
  padding: '14px 16px',
  border: `2px solid ${active ? '#4AB3BC' : '#E5E5E5'}`,
  borderRadius: '10px',
  background: active ? '#F3FAFB' : '#fff',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { borderColor: '#4AB3BC' },
}));

export const ChoiceTitle = styled('span', {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '15px',
  fontWeight: 700,
  color: active ? '#3196A0' : '#262626',
}));

export const ChoiceDesc = styled('span')({
  fontSize: '13px',
  color: '#737373',
  lineHeight: 1.5,
});

// 확인 단계 맨 위 — 지금 어떤 방법으로 승인하는지 한눈에
export const ModeBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '16px',
  padding: '6px 12px',
  borderRadius: '999px',
  fontSize: '13px',
  fontWeight: 700,
  color: '#3196A0',
  background: '#E9F6F7',
});

// 승인 창 맨 위 — 이 앱 계정이 예전에 이 지점과 연동했던 기록 안내
export const PreviousLinkInfo = styled(Box)({
  marginBottom: '16px',
  padding: '12px 14px',
  borderRadius: '8px',
  background: '#FFF8E6',
  border: '1px solid #F5D78E',
  fontSize: '14px',
  color: '#262626',
  lineHeight: 1.6,
});

export const PreviousLinkDates = styled('div')({
  fontSize: '13px',
  color: '#737373',
});

// 승인 창 — 잠재회원 새로 등록 안내 줄
export const NewTemporaryRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  flexWrap: 'wrap',
  marginTop: '16px',
  padding: '12px 14px',
  borderRadius: '8px',
  background: '#F3FAFB',
  fontSize: '14px',
  color: '#262626',
  lineHeight: 1.5,
});

// 거절 창 — 거절하면 생기는 일 안내
export const NoticeList = styled('ul')({
  margin: '16px 0 0',
  padding: '12px 14px 12px 30px',
  borderRadius: '8px',
  background: '#F5F5F5',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const NoticeItem = styled('li')({
  fontSize: '14px',
  color: '#525252',
  lineHeight: 1.6,
});

// 연동 확인 — 이름이 어떻게 바뀌는지 안내(경고보다 약하게)
export const NoticeText = styled(Typography)({
  marginTop: '8px',
  padding: '10px 12px',
  borderRadius: '8px',
  background: '#F3FAFB',
  fontSize: '13px',
  color: '#262626',
  lineHeight: 1.6,
});

export const WarningText = styled(Typography)({
  marginTop: '16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ef0c23',
  lineHeight: 1.6,
});

export const StatusRow = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
});

// 성별 칩 옆에 붙는 연동 상태 칩. 연동되면 청록 + 체크 아이콘, 초대 중이면 회색
export const LinkChip = styled('span', {
  shouldForwardProp: prop => prop !== 'linked',
})(({ linked }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 10px',
  borderRadius: '999px',
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  cursor: 'default',
  // 연동됨 — 잠재회원 화면과 같은 하늘색 계열(2026-10-23)
  color: linked ? '#3196A0' : '#6B7280',
  backgroundColor: linked ? '#E9F6F7' : '#F3F4F6',
}));

export const LinkButton = styled('button', {
  shouldForwardProp: prop => prop !== 'danger',
})(({ danger }) => ({
  height: '30px',
  padding: '0 12px',
  borderRadius: '6px',
  border: `1px solid ${danger ? '#ef0c23' : '#4AB3BC'}`,
  background: '#fff',
  color: danger ? '#ef0c23' : '#3196A0',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
}));

// 자동 승인(#195)으로 연동된 행 표시 — 상태 뱃지 아래 줄(상태 칸이 좁고 한 줄 고정이라)
export const AutoApprovedTag = styled('span')({
  display: 'block',
  width: 'fit-content',
  marginTop: '4px',
  padding: '1px 8px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#11979E',
  background: '#E8F6F7',
});
